import { z } from "zod";

import { SaleTypeForTerm } from "@/lib/gql/__generated__/graphql";
import { OptionInputSchema } from "@/lib/zod/__generated__/validation-schema";

import {
	chillnnDateZodSchema,
	dayOfTheWeekZodSchema,
	priceObjectZodSchema,
	requiredPrepMilliSecondBeforeDateZodSchema,
	requiredPrepMilliSecondBeforeHourZodSchema,
	s3ObjectZodSchema,
} from "@/components/features/_shared/validation-schema/validation-schema";

import { SubOptionsZodSchema } from "../../../../_shared/model/validation-schema";

export const optionInputZodSchema = OptionInputSchema.extend({
	name: z.string().min(1).max(30),
	description: z.string().min(1).max(5_000),

	mainKeyVisual: s3ObjectZodSchema,
	keyVisuals: z.array(s3ObjectZodSchema),

	requiredPrepMillisecondBeforeDate: requiredPrepMilliSecondBeforeDateZodSchema,
	requiredPrepMillisecondBeforeHour: requiredPrepMilliSecondBeforeHourZodSchema,

	maxBuyableNumPerTarget: z.number().int().min(1).max(1_000_000),

	minimumBuyableNumPerTarget: z
		.number()
		.int()
		.min(1)
		.max(100, { message: "100以下の数値を入力してください" })
		.nullish(),

	subOptions: SubOptionsZodSchema,

	maxDailyStockNum: z.number().int().min(1).max(9_999).nullish(),
	maxStockNum: z.number().int().min(1).max(99_999).nullish(),

	price: priceObjectZodSchema,

	stayableDayOfTheWeek: dayOfTheWeekZodSchema,
	isHolidayStayable: z.boolean().nullish(),

	saleStart: chillnnDateZodSchema.nullish().or(z.literal("")),
	saleEnd: chillnnDateZodSchema.nullish().or(z.literal("")),

	stayableStart: chillnnDateZodSchema.nullish(),
	stayableEnd: chillnnDateZodSchema.nullish(),

	saleStartBeforeDate: z.number().int().min(0).max(1_000, { message: "1000日以内に設定してください" }).nullish(),

	isNotDiscountTarget: z.boolean().optional(),

	isDateTabbedTimeSelectionEnabled: z.boolean(),
}) // ↓ 複数の値によるバリデーションを設定するために、refineを使用している
	// サブオプション
	.refine(
		({ useAsSubOption, subOptions }) => {
			if (useAsSubOption) {
				// サブオプションを設定する場合は、サブオプションが１つ以上設定されている必要がある
				return subOptions && subOptions.length > 0;
			}

			return true;
		},
		{
			message: "サブオプションを１つ以上設定してください",
			path: ["useAsSubOption"],
		},
	)
	// 公開する期間
	.refine(
		({ saleStart, saleEnd, saleTypeForTerm }) => {
			if (saleTypeForTerm === SaleTypeForTerm.Limited) {
				if (!!saleStart && !!saleEnd) {
					return saleStart <= saleEnd;
				}

				return true;
			}

			return true;
		},
		{
			message: "終了日が開始日以降になるように設定してください",
			path: ["saleEnd"],
		},
	)
	// 滞在可能な期間
	.refine(
		({ stayableStart, stayableEnd, saleTypeForTerm }) => {
			if (saleTypeForTerm === SaleTypeForTerm.Limited) {
				if (!!stayableStart && !!stayableEnd) {
					return stayableStart <= stayableEnd;
				}

				return !!stayableStart;
			}

			return true;
		},
		{
			message: "滞在可能な期間を設定してください",
			path: ["stayableStart"],
		},
	);

export type OptionInputZodSchemaType = z.infer<typeof optionInputZodSchema>;
